import { Link } from 'react-router-dom';
import clsx from 'clsx';
import {
  MapPin,
  Flag,
  Users,
  ChevronRight,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { Badge } from '../ui/Badge';

const statusConfig = {
  'Confirmed': { variant: 'success', icon: CheckCircle2 },
  'Scheduled': { variant: 'primary', icon: Clock },
  'Cancelled': { variant: 'error', icon: XCircle },
  'Postponed': { variant: 'warning', icon: AlertCircle },
  'Completed': { variant: 'neutral', icon: CheckCircle2 },
};

function seriesDotColor(color) {
  if (!color) return '#94a3b8';
  return color.startsWith('#') ? color : `#${color}`;
}

export default function EventCard({ event, seriesSlug, highlightWrg = false, showSeries = false }) {
  const slug = seriesSlug ?? event.primary_series_slug;
  const statusInfo = statusConfig[event.status] || { variant: 'neutral', icon: Clock };
  const isLive = Boolean(event.is_today);
  const showWrg = highlightWrg && Boolean(event.primary_series_slug);
  const seriesList = event.series_list || [];

  const content = (
    <div className={clsx(
      'flex items-center gap-3 p-2.5 bg-white border border-slate-200 rounded hover:border-wrg-blue-300 hover:shadow-sm transition-all',
      showWrg && 'border-l-2 border-l-wrg-blue-600'
    )}>
      <div className="flex-shrink-0 w-11 text-center bg-wrg-blue-600 rounded py-1 px-1">
        <div className="text-2xs text-white/70 uppercase leading-tight">
          {new Date(event.event_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short' })}
        </div>
        <div className="text-md font-bold text-white leading-tight">
          {new Date(event.event_date + 'T00:00:00').getDate()}
        </div>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <h3 className="text-sm font-semibold text-slate-800 group-hover:text-wrg-blue-600 transition-colors truncate">
            {event.name}
          </h3>
          {isLive && (
            <span className="inline-flex items-center gap-1 flex-shrink-0">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
              <span className="text-2xs font-semibold uppercase tracking-wide text-red-600">Live</span>
            </span>
          )}
          <Badge variant={statusInfo.variant} size="sm" className="flex-shrink-0">
            {event.status || 'Scheduled'}
          </Badge>
        </div>

        {showSeries && seriesList.length > 0 && (
          <div className="flex flex-wrap items-center gap-1 mt-1">
            {seriesList.slice(0, 3).map((s) => (
              <span
                key={s.name}
                className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-1.5 py-0.5 text-2xs font-medium text-slate-700"
              >
                <span
                  className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: seriesDotColor(s.color) }}
                />
                {s.name}
              </span>
            ))}
            {seriesList.length > 3 && (
              <span className="text-2xs text-slate-400">+{seriesList.length - 3}</span>
            )}
          </div>
        )}
        <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-500">
          <span className="flex items-center gap-1 truncate">
            <MapPin className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{event.track_name}</span>
            {event.city && event.state_abbr && (
              <span className="text-slate-400 hidden sm:inline">• {event.city}, {event.state_abbr}</span>
            )}
          </span>
          <span className="flex items-center gap-1 flex-shrink-0 text-slate-400">
            <Flag className="w-3 h-3" />
            {event.total_races_count || 0}
          </span>
          <span className="flex items-center gap-1 flex-shrink-0 text-slate-400">
            <Users className="w-3 h-3" />
            {event.entry_count || 0}
          </span>
        </div>
      </div>

      {slug && (
        <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-wrg-blue-600 transition-colors flex-shrink-0" />
      )}
    </div>
  );

  if (!slug) {
    return <div className="block cursor-default">{content}</div>;
  }

  return (
    <Link to={`/${slug}/events/${event.id}`} className="block group">
      {content}
    </Link>
  );
}
